import { create } from 'zustand';
import {
  CONNECTION_STATUS,
  FEED_FILTER,
  LINK_HYDRATION,
  LINK_LIMITS,
  SEND_TRANSPORT,
  TOPIC_ROLE,
  TRANSPORT_PREFERENCE,
  TOPIC_SUFFIX,
} from '../lib/rcu/catalog/link';
import type { ConnectionStatus, TransportPreference } from '../lib/rcu/catalog/link';
import { decodeLogBatch } from '../lib/rcu/link/decode';
import { demoCommands, demoMessages } from '../lib/rcu/link/demo';
import { incomingEvent, linkEvent, outgoingEvent } from '../lib/rcu/link/events';
import { hydrationReads } from '../lib/rcu/link/hydrate';
import type { RcuEvent } from '../lib/rcu/link/events';
import { EMPTY_LIVE, applyReading } from '../lib/rcu/link/live';
import type { LiveState } from '../lib/rcu/link/live';
import {
  DEFAULT_SESSION_DEPS,
  NO_SESSION_ROUTE,
  openBridgeSession,
  sendOverRoute,
} from '../lib/rcu/link/session';
import type { LinkSession, SendOutcome, SessionDeps } from '../lib/rcu/link/session';
import { hasBridgeAddress, pageOrigin } from '../lib/rcu/link/bridge';
import { LINK_ROUTE } from '../lib/rcu/catalog/link';
import type { LinkRoute } from '../lib/rcu/catalog/link';
import type { LinkHandlers, LinkState } from '../lib/rcu/link/types';
import { loadLinkSettings, saveLinkSettings } from '../lib/rcu/link/settings';
import { allTopics } from '../lib/rcu/link/topics';
import type { LinkSettings } from '../lib/rcu/link/settings';
import type { RcuConfig } from '../lib/rcu/model';
import type { RcuDocument } from '../lib/rcu/wire';

/**
 * The technician console's link to the RCU, and everything it has heard.
 *
 * Two halves, on purpose: the *feed* keeps history (what was sent, what came
 * back, what the link did), and `live` keeps the present, folded from the same
 * readings by `applyReading`. Panels read `live`; the drawer reads the feed.
 *
 * The session is created through `SessionDeps`, so tests drive the whole store
 * with a fake client and no broker. Nothing here holds a connection open after
 * the console locks: `disconnect` is called from the page's lock path.
 */

/** What the header chip shows, plus the topics the session subscribed to. */
export interface LinkStatus extends LinkState {
  readonly status: ConnectionStatus;
  readonly topics: readonly string[];
  /**
   * Which route the live session came up on. A first-class fact rather than
   * something to read out of `detail`, because the broker link and the bridge are
   * separate things and a technician has to be able to tell them apart.
   */
  readonly via: LinkRoute;
}

/** One line of the log tail, with the moment it arrived. */
export interface LogLine {
  readonly at: string;
  readonly text: string;
}

export interface RcuLinkStore {
  readonly settings: LinkSettings;
  readonly link: LinkStatus;
  readonly connecting: boolean;
  readonly live: LiveState;
  readonly events: readonly RcuEvent[];
  readonly droppedEvents: number;
  readonly paused: boolean;
  readonly logLines: readonly LogLine[];
  readonly droppedLogLines: number;
  readonly lastReadingAt: string | null;
  readonly inFlight: number;
  readonly demo: boolean;

  updateSettings: (patch: Partial<LinkSettings>) => void;
  /** Which channel every command from this tablet leaves on. */
  setTransport: (transport: TransportPreference) => void;
  connect: (config: RcuConfig) => Promise<boolean>;
  disconnect: () => void;
  /**
   * One round of reads for the channels this configuration knows about, spread
   * out so the firmware's 20-deep queue survives it. Called when the link comes
   * up and when the configuration changes — never on a timer.
   */
  hydrate: (config: RcuConfig) => void;
  send: (config: RcuConfig | null, document: RcuDocument) => Promise<SendOutcome>;
  clearFeed: () => void;
  setPaused: (paused: boolean) => void;
  clearLive: () => void;
  setDemo: (enabled: boolean) => void;
}

/** Swappable so a test can run the store against a fake client. */
let sessionDeps: SessionDeps = DEFAULT_SESSION_DEPS;

export const setSessionDeps = (deps: SessionDeps): void => {
  sessionDeps = deps;
};

export const resetSessionDeps = (): void => {
  sessionDeps = DEFAULT_SESSION_DEPS;
};

/** The live session, kept out of the store because it is not serialisable state. */
let session: LinkSession | null = null;
let sequence = 0;

/**
 * Which attempt at a session is the current one, and what it was opened with.
 *
 * Opening a session awaits a lazy chunk and a socket, so a connect can still be
 * in flight when the broker settings are cleared, when Disconnect is pressed, or
 * when the settle timer opens another one. The epoch is what makes the older
 * attempt harmless: it closes the client it just built instead of adopting it,
 * and its handlers stop writing to a store that has moved on.
 *
 * `sessionOpenedWith` makes a second connect with the credentials already live a
 * no-op, so pressing Connect — or the host's timer firing just after a manual
 * connect — cannot churn the socket.
 */
let connectEpoch = 0;
let sessionOpenedWith: string | null = null;

/** What a session's identity is for the purpose of not reopening it. */
const identityOf = (settings: LinkSettings, serial: string): string =>
  [settings.bridgeUrl, settings.bridgeUsername, settings.bridgePassword, serial].join('\u0000');

/**
 * The hydration in progress, if any. The token is what stops a half-sent
 * snapshot from continuing after a reconnect or a lock: the scheduled step
 * checks it is still the current run before sending anything.
 */
let hydrationToken = 0;
let hydrationTimer: ReturnType<typeof setTimeout> | null = null;

const cancelHydration = (): void => {
  hydrationToken += 1;
  if (hydrationTimer !== null) {
    clearTimeout(hydrationTimer);
    hydrationTimer = null;
  }
};

const nowIso = (): string => new Date().toISOString();

const nextIdentity = (at: string = nowIso()): { id: string; at: string } => {
  sequence += 1;
  return { id: `evt-${sequence}`, at };
};

const IDLE_LINK: LinkStatus = {
  status: CONNECTION_STATUS.IDLE,
  detail: 'No broker session.',
  topics: [],
  via: LINK_ROUTE.NONE,
};

/** Adds events, keeping the ring and counting what fell off. */
const appended = (
  events: readonly RcuEvent[],
  dropped: number,
  added: readonly RcuEvent[],
): { events: readonly RcuEvent[]; droppedEvents: number } => {
  const merged = [...events, ...added];
  if (merged.length <= LINK_LIMITS.EVENT_RING) {
    return { events: merged, droppedEvents: dropped };
  }
  const overflow = merged.length - LINK_LIMITS.EVENT_RING;
  return { events: merged.slice(overflow), droppedEvents: dropped + overflow };
};

export const useRcuLinkStore = create<RcuLinkStore>()((set, get) => {
  /**
   * One inbound message. Log batches only feed the tail — hundreds of lines
   * would bury the feed — and coredump chunks only advance the transfer, for the
   * same reason.
   */
  const ingest = (message: { topic: string; payload: string }, at: string): void => {
    /** Log lines arrive on their own topic and only feed the tail. */
    if (message.topic.endsWith(TOPIC_SUFFIX.logs)) {
      const batch = decodeLogBatch(message.payload);
      const lines = batch.lines.map((text) => ({ at, text }));
      const merged = [...get().logLines, ...lines];
      const overflow = Math.max(0, merged.length - LINK_LIMITS.LOG_RING);
      set((state) => ({
        logLines: overflow > 0 ? merged.slice(overflow) : merged,
        droppedLogLines: state.droppedLogLines + batch.dropped + overflow,
      }));
      return;
    }

    const event = incomingEvent(nextIdentity(at), { topic: message.topic, raw: message.payload });
    const updates: Partial<RcuLinkStore> = {
      live: applyReading(get().live, event.reading, at),
      lastReadingAt: at,
    };

    if (get().paused) {
      set({ ...updates, droppedEvents: get().droppedEvents + 1 });
      return;
    }
    /** Hundreds of chunks would bury the feed; the progress card shows them. */
    if (event.reading.kind === 'coredumpChunk') {
      set(updates);
      return;
    }

    set({ ...updates, ...appended(get().events, get().droppedEvents, [event]) });
  };

  /**
   * The link's own state, in the header chip and — when it changes — in the feed.
   * A broker that keeps retrying reports the same state every few seconds, so
   * only a change is worth a line.
   */
  const applyLinkState = (state: LinkState, at: string): void => {
    const changed = get().link.status !== state.status;
    set((store) => ({
      link: { ...state, topics: store.link.topics, via: store.link.via },
      ...(changed
        ? appended(store.events, store.droppedEvents, [
            linkEvent(nextIdentity(at), { summary: `Broker ${state.status}`, detail: state.detail }),
          ])
        : { events: store.events, droppedEvents: store.droppedEvents }),
    }));
  };

  /**
   * One command, whichever way the preference lets it leave, recorded in the
   * feed either way. Routed even with no session: the tablet's own HTTP port is
   * a real transport, and it is the one a room keeps when no broker is set up.
   */
  const dispatch = async (
    config: RcuConfig | null,
    document: RcuDocument,
    preference: TransportPreference,
  ): Promise<SendOutcome> => {
    set({ inFlight: get().inFlight + 1 });

    const outcome: SendOutcome = await sendOverRoute(
      session?.route ?? NO_SESSION_ROUTE,
      config,
      document,
      preference,
      sessionDeps.postHttp,
    );

    const at = nowIso();
    set((store) => ({
      inFlight: Math.max(0, store.inFlight - 1),
      ...appended(store.events, store.droppedEvents, [
        outgoingEvent(nextIdentity(at), {
          topic: outcome.topic,
          document,
          ok: outcome.ok,
          transport: outcome.transport,
          detail: outcome.detail,
        }),
      ]),
    }));
    return outcome;
  };

  return {
    settings: loadLinkSettings(),
    link: IDLE_LINK,
    connecting: false,
    live: EMPTY_LIVE,
    events: [],
    droppedEvents: 0,
    paused: false,
    logLines: [],
    droppedLogLines: 0,
    lastReadingAt: null,
    inFlight: 0,
    demo: false,

    updateSettings: (patch) => {
      const settings = { ...get().settings, ...patch };
      saveLinkSettings(settings);
      set({ settings });
    },

    setTransport: (transport) => {
      get().updateSettings({ transport });
    },

    connect: async (config) => {
      const { settings } = get();
      const serial = config.rcu.serialNumber.trim();
      const identity = identityOf(settings, serial);

      /** Already listening on these credentials: nothing to reopen. */
      if (
        session !== null &&
        sessionOpenedWith === identity &&
        get().link.status === CONNECTION_STATUS.ONLINE
      ) {
        return true;
      }

      connectEpoch += 1;
      const epoch = connectEpoch;
      session?.close();
      session = null;
      sessionOpenedWith = null;

      if (serial === '') {
        set({ connecting: false });
        applyLinkState(
          { status: CONNECTION_STATUS.ERROR, detail: 'Set the RCU serial number first.' },
          nowIso(),
        );
        return false;
      }

      set({ connecting: true, link: { ...get().link, status: CONNECTION_STATUS.CONNECTING, topics: [] } });

      /** True while this attempt is still the one the store is waiting on. */
      const current = (): boolean => epoch === connectEpoch;

      /**
       * No configured address means the gateway is wherever this app came from,
       * which is the arrangement the deployment expects: one host serving the
       * app and proxying the gateway on the same origin.
       */
      const bridge = {
        url: settings.bridgeUrl.trim() === '' ? pageOrigin() : settings.bridgeUrl,
        username: settings.bridgeUsername,
        password: settings.bridgePassword,
        serial,
      };

      if (!hasBridgeAddress(bridge)) {
        set({
          connecting: false,
          link: {
            ...IDLE_LINK,
            detail: 'No bridge address. It is normally wherever this app came from.',
          },
        });
        return false;
      }

      /** The handlers every client of this attempt reports through. */
      const storeHandlers = (): LinkHandlers => ({
        onMessage: (message) => {
          if (current()) ingest(message, nowIso());
        },
        onState: (state) => {
          if (!current()) return;
          applyLinkState(state, nowIso());
          if (state.status !== CONNECTION_STATUS.CONNECTING) set({ connecting: false });
        },
      });

      try {
        const opened = await openBridgeSession(bridge, storeHandlers(), sessionDeps);

        /** A disconnect, or a newer connect, happened while this one was starting. */
        if (!current()) {
          opened.close();
          return false;
        }

        session = opened;
        sessionOpenedWith = identity;
        set((store) => ({
          connecting: false,
          link: { ...store.link, topics: allTopics(opened.topics), via: LINK_ROUTE.BRIDGE },
        }));
        return true;
      } catch (error) {
        if (!current()) return false;
        set({ connecting: false });
        const detail = error instanceof Error ? error.message : 'The bridge client could not start.';
        applyLinkState({ status: CONNECTION_STATUS.ERROR, detail }, nowIso());
        return false;
      }
    },

    disconnect: () => {
      /** Anything still starting belongs to a session nobody wants any more. */
      connectEpoch += 1;
      sessionOpenedWith = null;
      cancelHydration();
      session?.close();
      session = null;
      set({ link: IDLE_LINK, connecting: false });
    },

    hydrate: (config) => {
      cancelHydration();
      const token = hydrationToken;
      const reads = hydrationReads(config);

      const step = (index: number): void => {
        if (token !== hydrationToken) return;
        const read = reads[index];
        if (read === undefined) {
          hydrationTimer = null;
          return;
        }
        /**
         * A read is sent on the broker even when the tablet is on HTTP,
         * whatever the preference says: the answer is the whole point of it, and
         * the device's own port cannot be read from a browser. A read with no
         * session is simply not sent.
         */
        if (read.document.ok) void dispatch(config, read.document.value, TRANSPORT_PREFERENCE.MQTT);
        hydrationTimer = setTimeout(() => step(index + 1), LINK_HYDRATION.SPACING_MS);
      };

      step(0);
    },

    send: async (config, document) => dispatch(config, document, get().settings.transport),

    clearFeed: () => set({ events: [], droppedEvents: 0 }),

    setPaused: (paused) => set({ paused }),

    clearLive: () => set({ live: EMPTY_LIVE, lastReadingAt: null }),

    /**
     * Replays the reference's recorded conversation. It goes through the same
     * decoder and the same live fold as a real session, so the console can be
     * reviewed without a device — and cannot drift from the real thing.
     */
    setDemo: (enabled) => {
      set({ demo: enabled });
      if (!enabled) return;

      const base = Date.now();
      const at = (offset: number): string => new Date(base + offset).toISOString();
      const events: RcuEvent[] = demoCommands().map((document, index) =>
        outgoingEvent(nextIdentity(at(index)), {
          topic: 'demo/toDeviceRCU',
          document,
          ok: true,
          transport: SEND_TRANSPORT.MQTT,
          detail: 'Replayed from the recorded conversation.',
        }),
      );

      let live = get().live;
      const lines: LogLine[] = [];

      demoMessages().forEach((message, index) => {
        const moment = at(events.length + index);
        if (message.topicRole === TOPIC_ROLE.LOGS) {
          decodeLogBatch(message.raw).lines.forEach((text) => lines.push({ at: moment, text }));
          return;
        }
        const event = incomingEvent(nextIdentity(moment), { topic: 'demo/toCloudRCU', raw: message.raw });
        live = applyReading(live, event.reading, moment);
        events.push(event);
      });

      set((store) => ({
        live,
        lastReadingAt: at(events.length),
        logLines: [...store.logLines, ...lines],
        ...appended(store.events, store.droppedEvents, events),
      }));
    },
  };
});

/** The filters the drawer's chips offer, in display order. */
export const FEED_FILTERS = [FEED_FILTER.ALL, FEED_FILTER.OUT, FEED_FILTER.IN, FEED_FILTER.ERRORS] as const;
